import { z } from 'zod';

import { CheckoutModeSchema, PurchaseOriginSchema } from '../indicator/indicator';

/** Os dois vínculos de viagem nunca andam juntos (#3712). Exportado porque os
 *  validadores por modal do modal-session reconstroem o schema via `.shape`. */
export function refineWriteAheadTripLink(
  value: { resolvedTripId?: unknown; preferredTripId?: unknown },
  ctx: z.RefinementCtx,
): void {
  if (value.resolvedTripId && value.preferredTripId) {
    ctx.addIssue({
      code: 'custom',
      message: '`resolvedTripId` and `preferredTripId` are mutually exclusive.',
      path: ['preferredTripId'],
    });
  }
}

export const ModalSessionBookingWriteAheadSchema = z
  .object({
    requesterId: z.string().min(1),
    travelerId: z.string().min(1).optional(),
    companyId: z.string().min(1),
    conversationId: z.string().min(1).optional(),
    // `resolvedTripId` NOMEIA a viagem (rota trip-scoped, sem régua): só para quem a
    // escolheu de fato — ex. os trechos 2+ de um roteiro multi-trecho seguem o 1º.
    // `preferredTripId` é a viagem da CONVERSA (#3712): preferência que a régua do
    // trip-api só honra quando cidade/datas casam; incoerente, vira outra viagem.
    resolvedTripId: z.string().min(1).optional(),
    preferredTripId: z.string().min(1).optional(),
    // Onde a compra começou e onde o checkout foi enviado (#5536): o
    // modal-session repassa os dois ao pedido no trip-api.
    purchaseOrigin: PurchaseOriginSchema.optional(),
    checkoutMode: CheckoutModeSchema.optional(),
    // Flight XOR hotel: exactly one of `itinerary` / `stay` is present (enforced
    // by the superRefine below). Flight `itinerary` keeps its exact shape so the
    // flight payload stays byte-for-byte valid; hotels carry `stay` instead.
    itinerary: z
      .object({
        origin: z.string().min(1),
        destination: z.string().min(1),
        departureDate: z.iso.date(),
        returnDate: z.iso.date().optional(),
      })
      .strict()
      .optional(),
    stay: z
      .object({
        city: z.string().min(1),
        checkIn: z.iso.date(),
        checkOut: z.iso.date(),
      })
      .strict()
      .optional(),
    requestedAtWindow: z.object({ start: z.iso.datetime(), end: z.iso.datetime() }).strict(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (Boolean(value.itinerary) === Boolean(value.stay)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Exactly one of `itinerary` (flight) or `stay` (hotel) must be present.',
        path: value.itinerary ? ['stay'] : ['itinerary'],
      });
    }
    refineWriteAheadTripLink(value, ctx);
  });
export type ModalSessionBookingWriteAhead = z.infer<typeof ModalSessionBookingWriteAheadSchema>;
